import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Card, CardHeader, CardBody } from '../ui/Card';
import { StatusBadge } from '../ui/StatusBadge';
import { useAsync } from '../../hooks/useAsync';
import { api, ApiError } from '../../api/client';
import { showToast } from '../Toaster';
import type {
  ClientListItemDto,
  ClientRuntimeInfoPayload,
  ClientStorageUsage,
  ClientTelemetryDto,
  ListClientsResponse,
} from '../../api/types';
import { SerialInterfacesModal } from '../SerialInterfacesModal';
import { PanelManageModal } from '../PanelManageModal';
import type { TouchpanelPlatform } from '../touchpanelModel';
import './DeviceClientInfo.css';

interface Props {
  plantId: string;
  /** Fires whenever the client fetch resolves. Parent uses this to dedupe
   *  the same interfaces out of the "Verfügbare Geräte" list — the ScanCandidate
   *  rows with isSelf=true are the exact same physical box we render here. */
  onClientsChange?: (clients: ClientListItemDto[]) => void;
}

/** Collapsible section at the top of the Devices page listing every field-
 *  client registered on this plant together with its latest telemetry sample.
 *  Fetches `GET /api/clients?plantId={plantId}` and polls every 10 s so the
 *  live vitals (CPU, RAM, uptime) refresh without a SignalR channel. */
export function DeviceClientInfo({ plantId, onClientsChange }: Props) {
  const [open, setOpen] = useState(true);
  const [tick, setTick] = useState(0);
  const [serialModal, setSerialModal] = useState<ClientListItemDto | null>(null);
  const [panelModal, setPanelModal] = useState<ClientListItemDto | null>(null);
  const state = useAsync(
    (signal) =>
      api.get<ListClientsResponse>(
        `/api/clients?plantId=${encodeURIComponent(plantId)}`,
        signal,
      ),
    [plantId, tick],
  );

  // Refresh cadence: 10 s. Uses tick-based re-fetch instead of setInterval on
  // the fetcher so useAsync's abort-on-unmount stays wired up cleanly.
  useIntervalTick(() => setTick((t) => t + 1), 10_000);

  const clients = state.status === 'ok' ? state.data.clients : [];
  const clientCount = clients.length;

  // Bubble the current client list up to the parent so it can dedupe the
  // isSelf ScanCandidate rows. Fires on every fetch resolution.
  useEffect(() => {
    if (state.status === 'ok' && onClientsChange) onClientsChange(state.data.clients);
  }, [state.status, state.status === 'ok' ? state.data : null, onClientsChange]);

  return (
    <Card padded={false} class="hv-client-info">
      <CardHeader>
        <button
          type="button"
          class="hv-client-info__toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          title={open ? 'Client-Info einklappen' : 'Client-Info ausklappen'}
        >
          <span class="hv-client-info__caret">{open ? '▾' : '▸'}</span>
          <span class="hv-client-info__title">Field-Clients</span>
          {state.status === 'ok' && (
            <StatusBadge tone="neutral">
              {clientCount === 1 ? '1 Client' : `${clientCount} Clients`}
            </StatusBadge>
          )}
        </button>
      </CardHeader>
      {open && (
        <CardBody>
          {state.status === 'loading' && (
            <p class="muted">Lade Client-Info…</p>
          )}
          {state.status === 'error' && (
            <p class="muted">Client-Info konnte nicht geladen werden.</p>
          )}
          {state.status === 'ok' && clients.length === 0 && (
            <OrphanClientAdopter plantId={plantId} onAdopted={() => setTick((t) => t + 1)} />
          )}
          {state.status === 'ok' && clients.length > 0 && (
            <div class="hv-client-info__grid">
              {clients.map((c) => (
                <ClientCard key={c.runtimeInfo.clientId} item={c}
                            onOpenSerialInterfaces={() => setSerialModal(c)}
                            onOpenPanel={() => setPanelModal(c)} />
              ))}
            </div>
          )}
        </CardBody>
      )}
      {serialModal && (
        <SerialInterfacesModal
          plantId={plantId}
          clientId={serialModal.runtimeInfo.clientId}
          clientLabel={serialModal.runtimeInfo.hostname || serialModal.runtimeInfo.clientId.slice(0, 8)}
          runtimePorts={serialModal.runtimeInfo.serialPorts ?? undefined}
          // productId kommt aus PlantOverview-Endpoint; ClientListItemDto
          // führt es aktuell nicht mit. Ohne productId lädt der Modal keinen
          // Katalog und fällt auf freie Device-Eingabe zurück.
          onClose={() => setSerialModal(null)}
        />
      )}
      {panelModal && (
        <PanelManageModal
          plantId={plantId}
          clientId={panelModal.runtimeInfo.clientId}
          clientLabel={panelModal.runtimeInfo.hostname || panelModal.runtimeInfo.clientId.slice(0, 8)}
          platform={touchpanelPlatformOf(panelModal) ?? 'sinnux'}
          onClose={() => setPanelModal(null)}
        />
      )}
    </Card>
  );
}

interface OrphanClientInfo {
  id: string;
  hostname: string;
  chassisDisplayName: string | null;
  chassisVendor: string | null;
  chassisProduct: string | null;
  osPretty: string | null;
  machineId: string | null;
  appVersion: string | null;
  reportedAt: string;
}

function OrphanClientAdopter({ plantId, onAdopted }: { plantId: string; onAdopted(): void }) {
  const orphans = useAsync(
    (signal) => api.get<{ orphans: OrphanClientInfo[] }>(`/api/clients/orphans`, signal),
    [plantId],
  );
  const list = orphans.status === 'ok' ? orphans.data.orphans : [];
  const adopt = async (clientId: string, label: string) => {
    try {
      await api.post(`/api/plants/${encodeURIComponent(plantId)}/clients/${clientId}/adopt`, {});
      showToast({
        tone: 'success',
        title: 'FieldClient wieder verbunden',
        message: label,
        autoHideAfterMs: 4000,
      });
      onAdopted();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError
        ? (err.body as { error?: string; detail?: string; title?: string } | undefined)
        : undefined;
      const serverMsg = body?.error ?? body?.detail ?? body?.title;
      showToast({
        tone: 'error',
        title: 'Adopt fehlgeschlagen',
        message: err instanceof ApiError
          ? `HTTP ${status}${serverMsg ? ' — ' + serverMsg : ''}`
          : ((err as Error)?.message ?? 'Unbekannter Fehler'),
        autoHideAfterMs: 8000,
      });
    }
  };
  return (
    <div>
      <p class="muted" style={{ marginTop: 0 }}>
        Keine Field-Clients auf dieser Anlage registriert.
      </p>
      {orphans.status === 'loading' && <p class="muted">Lade Orphan-Records…</p>}
      {orphans.status === 'ok' && list.length === 0 && (
        <p class="muted" style={{ fontSize: 12 }}>
          Auch keine Orphan-Records am Broker vorhanden. Der Client-Runtime muss neu
          gestartet werden damit er sich meldet, oder per Kickstarter neu provisionieren.
        </p>
      )}
      {orphans.status === 'ok' && list.length > 0 && (
        <div>
          <p class="muted" style={{ fontSize: 12, marginTop: 0 }}>
            {list.length} Orphan-Runtime-Info-Zeile(n) am Server. Wähle die richtige für diese Anlage:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {list.map((o) => (
              <div key={o.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, padding: '8px 10px',
                border: '1px solid var(--border-subtle)', borderRadius: 6,
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {o.hostname || '(kein Hostname)'}
                    {o.chassisDisplayName && <span class="muted" style={{ marginLeft: 8, fontWeight: 400 }}>· {o.chassisDisplayName}</span>}
                  </div>
                  <div class="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    <code>{o.id}</code>
                    {o.osPretty && <> · {o.osPretty}</>}
                    {o.appVersion && <> · v{o.appVersion}</>}
                    <> · gemeldet {formatRelative(o.reportedAt)}</>
                  </div>
                </div>
                <button
                  type="button"
                  class="hv-btn hv-btn--sm hv-btn--primary"
                  onClick={() => adopt(o.id, o.hostname || o.id.slice(0, 8))}
                >
                  Dieser Anlage zuweisen
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ClientCardProps {
  item: ClientListItemDto;
  onOpenSerialInterfaces(): void;
  onOpenPanel(): void;
}

/** Touchpanels lassen sich über manage.v1 einstellen und updaten
 *  (docs/wire-device-management.md 6.1 und 6.3), Android wie sinnux.
 *  Erkennbar am Betriebssystem; dieselbe Regel wie TouchpanelClassifier auf
 *  dem Server. */
export function touchpanelPlatformOf(item: ClientListItemDto): TouchpanelPlatform | null {
  if (item.runtimeInfo.os.kind === 'android') return 'android';
  if (/sinnux/i.test(item.runtimeInfo.os.pretty ?? '')) return 'sinnux';
  return null;
}

function ClientCard({ item, onOpenSerialInterfaces, onOpenPanel }: ClientCardProps) {
  const info = item.runtimeInfo;
  const telemetry = item.telemetry;
  const tone = statusTone(item);
  const toneLabel = statusLabel(item);

  return (
    <article class="hv-client-card">
      <header class="hv-client-card__header">
        <span
          class={`hv-client-card__dot hv-client-card__dot--${tone}`}
          title={toneLabel}
          aria-label={toneLabel}
        />
        <div class="hv-client-card__id">
          <span class="hv-client-card__hostname">{info.hostname || info.clientId}</span>
          <span class="hv-client-card__chassis muted">
            {info.chassis.displayName || info.chassis.product || 'Unbekannte Hardware'}
            {info.hardware?.serial && (
              <span class="hv-client-card__serial mono" title="Hardware-Seriennummer">
                {' · '}{info.hardware.serial}
              </span>
            )}
          </span>
        </div>
        <StatusBadge tone={tone === 'ok' ? 'ok' : tone === 'warn' ? 'warn' : 'error'}>
          {toneLabel}
        </StatusBadge>
      </header>

      <div class="hv-client-card__body">
        {/* Spalte 1 — Statische Meta, kompakt als KV. Fasst die vier "einmal
            lesen und gut ist"-Zeilen zusammen so dass der Rest der Karte nicht
            fuer sie nach unten wachsen muss. */}
        <dl class="hv-client-card__kv">
          <dt>OS</dt>
          <dd>
            <span>{info.os.pretty || '—'}</span>
            {info.os.kernel && (
              <span class="muted mono"> · {info.os.kernel}</span>
            )}
          </dd>

          <dt>Client</dt>
          <dd>
            <span class="mono">{info.app.version || '—'}</span>
            {info.app.runtime && (
              <span class="muted"> · {info.app.runtime}</span>
            )}
          </dd>

          <dt>CPU</dt>
          <dd>
            <span>{info.cpu.model && info.cpu.model !== 'Unknown' ? info.cpu.model : 'Unbekannt'}</span>
            <span class="muted"> · {info.cpu.cores} Kern{info.cpu.cores === 1 ? '' : 'e'}</span>
          </dd>

          <dt>Betriebszeit</dt>
          <dd>
            {telemetry ? (
              <span class="mono">{formatUptime(telemetry.uptimeSeconds)}</span>
            ) : (
              <span class="muted">—</span>
            )}
          </dd>

          <dt>Zuletzt</dt>
          <dd>
            {telemetry ? (
              <span title={telemetry.reportedAt}>{formatRelative(telemetry.reportedAt)}</span>
            ) : (
              <span title={info.reportedAt} class="muted">
                Runtime-Info {formatRelative(info.reportedAt)}
              </span>
            )}
          </dd>
        </dl>

        {/* Spalte 2 — Live-Ressourcen. Bars fuellen die Spalten-Breite aus,
            Label rechts daneben. Ein Metrik-Block pro Zeile. */}
        <div class="hv-client-card__resources">
          <ResourceLine
            label="Auslastung"
            fallback={telemetry ? null : 'Noch kein Sample.'}
            bar={telemetry ? (
              <UsageBar
                percent={clamp01(telemetry.cpuPercent / 100)}
                label={`${telemetry.cpuPercent.toFixed(0)}%`}
              />
            ) : null}
          />
          <ResourceLine
            label="RAM"
            fallback={telemetry ? null : `${formatBytes(info.totalRamBytes)} verbaut`}
            bar={telemetry ? (
              <UsageBar
                percent={telemetry.memTotalBytes > 0 ? telemetry.memUsedBytes / telemetry.memTotalBytes : 0}
                label={`${formatBytes(telemetry.memUsedBytes)} / ${formatBytes(telemetry.memTotalBytes)}`}
              />
            ) : null}
          />
          <StorageBlock runtime={info} telemetry={telemetry} />
        </div>

        {/* Spalte 3 — Netzwerk-Interfaces. Immer sichtbar, weil ein Field-Client
            IMMER mindestens ein Interface hat; ohne wuerde er sich gar nicht
            connecten koennen. Wenn info.network doch leer bleibt: Hinweis. */}
        <div class="hv-client-card__net">
          <div class="hv-client-card__section-label">Netzwerk</div>
          {info.network.length === 0 ? (
            <span class="muted">Keine Interfaces gemeldet.</span>
          ) : (
            info.network.map((n) => (
              <div key={n.iface} class="hv-client-card__net-row">
                <span class="mono hv-client-card__net-iface">{n.iface}</span>
                <span class="mono">{n.ip}</span>
                <span class="mono muted hv-client-card__net-mac">{n.mac}</span>
              </div>
            ))
          )}
          <div style={{ marginTop: 8 }}>
            {/* Standardmaessig zu: ein Panel meldet bis zu zehn UARTs (ttyS0..9), und die
                fuellten die Karte komplett. Die Zahl steht im Titel, aufgeklappt wird bei Bedarf. */}
            <details class="hv-client-card__serial">
              <summary class="hv-client-card__section-label" style={{ cursor: 'pointer' }}>
                Serielle Busse{info.serialPorts ? ` (${info.serialPorts.length})` : ''}
              </summary>
              {info.serialPorts && info.serialPorts.length > 0 ? (
                info.serialPorts.map((p) => (
                  <div key={p.device} class="hv-client-card__net-row" title={
                    `${p.driver ?? ''} ${p.uartType ?? ''}`.trim() +
                    (p.rxBytes !== undefined ? ` · rx ${p.rxBytes}` : '') +
                    (p.txBytes !== undefined ? ` · tx ${p.txBytes}` : '')
                  }>
                    <span class="mono hv-client-card__net-iface">{p.name}</span>
                    <span class="mono muted">{p.device}</span>
                    {p.role && <span class="mono muted">{p.role}</span>}
                  </div>
                ))
              ) : (
                <span class="muted" style={{ fontSize: '0.85rem' }}>Keine UARTs gemeldet.</span>
              )}
            </details>
            <button type="button" class="hv-btn hv-btn--sm" style={{ marginTop: 6 }}
                    onClick={onOpenSerialInterfaces}
                    title="Serial-Interfaces (Modbus-RTU-Konfiguration) verwalten">
              Serial-Interfaces…
            </button>
            {touchpanelPlatformOf(item) && (
              <button type="button" class="hv-btn hv-btn--sm hv-btn--primary" style={{ marginTop: 6, marginLeft: 6 }}
                      onClick={onOpenPanel}
                      title="Updates, Anzeige, Kiosk, Netz und VPN des Touchpanels aus der Ferne einstellen">
                Touchpanel verwalten…
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

interface ResourceLineProps {
  label: string;
  bar: ComponentChildren;
  fallback: string | null;
}

function ResourceLine({ label, bar, fallback }: ResourceLineProps) {
  return (
    <div class="hv-client-card__resource">
      <div class="hv-client-card__section-label">{label}</div>
      {bar ?? <span class="muted">{fallback}</span>}
    </div>
  );
}

interface StorageBlockProps {
  runtime: ClientRuntimeInfoPayload;
  telemetry: ClientTelemetryDto | null;
}

function StorageBlock({ runtime, telemetry }: StorageBlockProps) {
  const rows = telemetry?.storage ?? [];
  const empty = rows.length === 0 && runtime.storage.length === 0;
  return (
    <div class="hv-client-card__resource">
      <div class="hv-client-card__section-label">Datenträger</div>
      {empty && <span class="muted">—</span>}
      {rows.length > 0 && rows.map((s) => <StorageRow key={s.mount} entry={s} />)}
      {rows.length === 0 && runtime.storage.length > 0 && runtime.storage.map((s) => (
        <div key={s.mount} class="hv-client-card__storage-row">
          <span class="mono hv-client-card__storage-mount">{s.mount}</span>
          <span class="muted">{formatBytes(s.totalBytes)}</span>
        </div>
      ))}
    </div>
  );
}

function StorageRow({ entry }: { entry: ClientStorageUsage }) {
  const percent = entry.totalBytes > 0 ? entry.usedBytes / entry.totalBytes : 0;
  const p = clamp01(percent);
  const tone = p >= 0.9 ? 'error' : p >= 0.75 ? 'warn' : 'ok';
  const label = `${formatBytes(entry.usedBytes)} / ${formatBytes(entry.totalBytes)}`;
  // Bar-Track + Label direkt in den Storage-Row-Grid statt via UsageBar-
  // Wrapper — sonst haetten wir zwei geschachtelte 3-Spalten-Grids und die
  // Datentraeger-Bar wuerde sichtbar schmaler ausfallen als Auslastung/RAM.
  return (
    <div class="hv-client-card__storage-row">
      <span class="mono hv-client-card__storage-mount">{entry.mount}</span>
      <div class="hv-client-card__bar-track" aria-hidden>
        <div
          class={`hv-client-card__bar-fill hv-client-card__bar-fill--${tone}`}
          style={{ width: `${Math.round(p * 100)}%` }}
        />
      </div>
      <span class="hv-client-card__bar-label mono">{label}</span>
    </div>
  );
}

interface UsageBarProps {
  /** 0..1 clamped */
  percent: number;
  label: string;
}

function UsageBar({ percent, label }: UsageBarProps) {
  const p = clamp01(percent);
  const tone = p >= 0.9 ? 'error' : p >= 0.75 ? 'warn' : 'ok';
  return (
    <div class="hv-client-card__bar">
      <div class="hv-client-card__bar-track" aria-hidden>
        <div
          class={`hv-client-card__bar-fill hv-client-card__bar-fill--${tone}`}
          style={{ width: `${Math.round(p * 100)}%` }}
        />
      </div>
      <span class="hv-client-card__bar-label mono">{label}</span>
    </div>
  );
}

// --- Presenter helpers ----------------------------------------------------

type StatusTone = 'ok' | 'warn' | 'error';

function statusTone(item: ClientListItemDto): StatusTone {
  if (item.telemetry === null) return 'error';
  if (item.isStale) return 'warn';
  const ageMs = Date.now() - Date.parse(item.telemetry.reportedAt);
  if (Number.isNaN(ageMs)) return 'warn';
  if (ageMs < 60_000) return 'ok';
  if (ageMs < 300_000) return 'warn';
  return 'error';
}

function statusLabel(item: ClientListItemDto): string {
  if (item.telemetry === null) return 'Kein Sample';
  if (item.isStale) return 'Veraltet';
  const ageMs = Date.now() - Date.parse(item.telemetry.reportedAt);
  if (ageMs < 60_000) return 'Online';
  if (ageMs < 300_000) return 'Verzögert';
  return 'Veraltet';
}

function clamp01(v: number): number {
  if (Number.isNaN(v) || !Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  let idx = 0;
  let v = bytes;
  while (v >= 1024 && idx < BYTE_UNITS.length - 1) {
    v /= 1024;
    idx += 1;
  }
  const digits = v >= 100 || idx === 0 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(digits)} ${BYTE_UNITS[idx]}`;
}

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
}

function formatRelative(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'gerade eben';
  const s = Math.round(diffMs / 1000);
  if (s < 60) return `vor ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `vor ${m}min`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h}h`;
  const d = Math.round(h / 24);
  return `vor ${d}d`;
}

// --- Hook -----------------------------------------------------------------

/** Fires `cb` every `intervalMs` for as long as the component is mounted.
 *  Kept local because this is the only page that needs it — the polling
 *  interval is tuned to the ~5 s telemetry cadence with a bit of slack. */
function useIntervalTick(cb: () => void, intervalMs: number) {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const handle = window.setInterval(() => cbRef.current(), intervalMs);
    return () => window.clearInterval(handle);
  }, [intervalMs]);
}
